import { render, Fragment } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';

import {
  CategoricalFacet,
  NumericRangeFacet,
  BooleanFacet,
} from './components';

import {
  SearchBarOptions,
  FacetGroup,
  SearchHit,
  FacetExpansionState,
  AutocompleteSuggestion,
} from './types';

import { ApiClient } from './apiClient';
import { 
    ICON_FACETS, 
    ICON_BACK,
    ICON_CLOSE, 
    ICON_CLEAR,
    ICON_TOGGLE_FACETS, 
    ICON_SPEECH_RECOGNITION, 
    ICON_FLASH
} from './constants/icons';

import './styles/styles.scss';

// Re-export types for external consumers
export type {
  SearchBarOptions,
  SearchResult,
  FacetGroup,
  SearchHit,
  SearchState,
  AutocompleteSuggestion,
} from './types';

const isStockAvailable = (stock: any): boolean => {
  if (!stock) return false;
  const stockStr = String(stock).toLowerCase();
  if (stockStr === 'in stock') return true;
  const num = parseInt(stockStr, 10);
  return !isNaN(num) && num > 0;
};

const createResultTemplate =
  (addToCartEndpoint?: string) =>
  (item: SearchHit): string => {
    const imageHtml = item['image link']
      ? `<div class="result-image"><img src="${item['image link']}" alt="${item.title || ''}" /></div>`
      : '';

    const priceHtml =
      typeof item.sale_price === 'number' &&
      typeof item.price === 'number' &&
      item.sale_price < item.price
        ? `
          <span class="original-price">${item.price.toFixed(2)} €</span>
          <span class="sale-price">${item.sale_price.toFixed(2)} €</span>
          `
        : typeof item.price === 'number'
          ? `<span class="price">${item.price.toFixed(2)} €</span>`
          : '';

    const isAvailable = isStockAvailable(item.stock);
    const stockClass = isAvailable ? 'available' : 'unavailable';
    const stockText = isAvailable ? 'disponibile' : 'non disponibile';

    const stockHtml =
      isAvailable && addToCartEndpoint
        ? `<button data-product-id="${item.id}" class="add-to-cart">Acquista</button>`
        : '';

    return `
    <a class="result-content" href="${item.link || '#'}">
      ${imageHtml}
      <div class="result-details">
        <div class="result-description">${item.title || ''}</div>
        <div class="result-info">
          <div class="result-price">${priceHtml}</div>
          <div class="result-availability ${stockClass}">
            <span>${stockText}</span>
            ${stockHtml}
          </div>
        </div>
      </div>
    </a>
  `;
  };

interface SearchBarComponentProps extends SearchBarOptions {}

function SearchBarComponent(props: SearchBarComponentProps) {
  // Use a ref to keep a single instance of ApiClient across renders
  // This prevents recreating the WebSocket connection on every render
  const apiClientRef = useRef<ApiClient | null>(null);
  if (!apiClientRef.current) {
    const useWebsocket = process.env.USE_WEBSOCKET === 'true';
    apiClientRef.current = new ApiClient(
      props.apiKey!,
      props.clientId!,
      props.apiBaseUrl,
      useWebsocket // Use configured WebSocket support
    );
  }
  const apiClient = apiClientRef.current;

  const [query, setQuery] = useState(props.initialState?.query || '');
  const [results, setResults] = useState<SearchHit[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [facets, setFacets] = useState<FacetGroup[]>([]);
  const [selectedFacets, setSelectedFacets] = useState<{
    [key: string]: string[][];
  }>(props.initialState?.selectedFacets || {});
  const [expandedFacets, setExpandedFacets] = useState<FacetExpansionState>(
    props.initialState?.expandedFacets || {}
  );
  const [selectedPriceRange, setSelectedPriceRange] = useState<
    [number, number] | null
  >(props.initialState?.selectedPriceRange || null);
  const [userChangedPrice, setUserChangedPrice] = useState(false);

  // Sorting and simple boolean facets
  const [selectedSort, setSelectedSort] = useState<string>('relevance');
  const sortDropdownRef = useRef<HTMLDivElement | null>(null);
  const [booleanFacets, setBooleanFacets] = useState<Record<string, boolean>>(
    {}
  );

  // Autocomplete states
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<
    AutocompleteSuggestion[]
  >([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [topSuggestion, setTopSuggestion] = useState('');
  const [autocompleteTimeout, setAutocompleteTimeout] =
    useState<NodeJS.Timeout | null>(null);

  // Search debouncing states
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Request sequence tracking to ignore stale results
  const [searchSequence, setSearchSequence] = useState(0);
  const [autocompleteSequence, setAutocompleteSequence] = useState(0);

  // Mobile facet modal state
  const [showMobileFacets, setShowMobileFacets] = useState(false);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null); // #ff-arno-wm-container
  const innerRef = useRef<HTMLDivElement | null>(null); // #ff-arno-wm

   const resetSearchState = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    setShowAutocomplete(false);
    setAutocompleteSuggestions([]);
    setTopSuggestion('');
    setSelectedFacets({});
    setSelectedPriceRange(null);
    setUserChangedPrice(false);
    setBooleanFacets({});
    setSelectedSort('relevance');
    setExpandedFacets({});
    setShowMobileFacets(false);
  };

   // Reset state when closing fullscreen
  useEffect(() => {
    if (!isFullscreen) {
      resetSearchState();
    }
  }, [isFullscreen]);

  // Block scroll body when fullscreen
  useEffect(() => {
    document.body.style.overflow = isFullscreen ? 'hidden' : '';
  }, [isFullscreen]);

  // Intercept clicks on the target element to open fullscreen search
  useEffect(() => {
    if (!props.target) return;

    const targetEl = document.querySelector(props.target);
    if (!targetEl) {
      console.warn(`SearchBar target element not found: ${props.target}`);
      return;
    }

    const handleClick = (e: MouseEvent) => {
      if (targetEl.contains(e.target as Node)) {
        setIsFullscreen(true);
        const input = containerRef.current?.querySelector(
          'input.search-bar-input'
        ) as HTMLInputElement;
        if (input) {
          setQuery('');
          setShowAutocomplete(false);
          setResults([]);
          input.focus();
        }
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [props.target]);

  // ESC closes fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Click outside of inner container closes fullscreen
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isFullscreen) return;
      const target = e.target as Node;

      if (
        containerRef.current &&
        innerRef.current &&
        containerRef.current.contains(target) &&
        !innerRef.current.contains(target)
      ) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFullscreen]);

  const closeFullscreen = () => setIsFullscreen(false);

  const mergeBooleanFacets = (
    currentFacets: { [key: string]: string[][] },
    boolFacets: Record<string, boolean> | null | undefined
  ) => {
    const merged = { ...currentFacets };
    if (!boolFacets) return merged;

    Object.entries(boolFacets).forEach(([field, isChecked]) => {
      if (isChecked) {
        merged[field] = [['true']];
      }
    });
    return merged;
  };

  const handleImageLoad = (event: Event) => {
    const img = event.target as HTMLImageElement;
    img.classList.add('loaded');
  };

  const fetchAutocomplete = async (query: string) => {
    if (!query || query.length === 0) {
      setAutocompleteSuggestions([]);
      setShowAutocomplete(false);
      setTopSuggestion('');
      return;
    }

    // Increment sequence to track this request
    const currentSequence = autocompleteSequence + 1;
    setAutocompleteSequence(currentSequence);

    try {
      const data = await apiClient.fetchAutocomplete(query);

      // Only update UI if this result is more recent than what we currently have
      if (currentSequence >= autocompleteSequence) {
        setAutocompleteSuggestions(data.suggestions);
        setShowAutocomplete(data.suggestions.length > 0);

        // Set top suggestion for overlay
        if (data.suggestions.length > 0) {
          const topSugg = data.suggestions[0].text;
          if (
            topSugg.toLowerCase().startsWith(query.toLowerCase()) &&
            query.length > 0
          ) {
            setTopSuggestion(topSugg);
          } else {
            setTopSuggestion('');
          }
        } else {
          setTopSuggestion('');
        }
      }
      // If currentSequence !== autocompleteSequence, this is a stale response - ignore it
    } catch (error) {
      console.error('Autocomplete fetch error:', error);
    }
  };

  const handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const newQuery = target.value;
    setQuery(newQuery);

    // Clear previous autocomplete timeout
    if (autocompleteTimeout) {
      clearTimeout(autocompleteTimeout);
    }

    // Clear previous search timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Debounce autocomplete calls (faster for better UX)
    const autocompleteTimeoutId = setTimeout(() => {
      fetchAutocomplete(newQuery);
    }, 150);
    setAutocompleteTimeout(autocompleteTimeoutId);

    // Debounce search calls (longer delay to reduce server load)
    if (newQuery.length > 2) {
      const searchTimeoutId = setTimeout(() => {
        handleSearch(newQuery);
      }, 300);
      setSearchTimeout(searchTimeoutId);
    } else {
      // Clear results immediately if query is too short
      setResults([]);
      setShowResults(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      setShowAutocomplete(false);
    } else if (e.key === 'Enter') {
      // Immediate search on Enter, cancel any pending debounced searches
      e.preventDefault();
      if (autocompleteTimeout) {
        clearTimeout(autocompleteTimeout);
      }
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      if (query.length > 0) {
        handleSearch(query);
      }
    } else if (e.key === 'Tab' && topSuggestion && topSuggestion !== query) {
      e.preventDefault();
      const completedQuery = topSuggestion + ' ';
      setQuery(completedQuery);
      setShowAutocomplete(false);

      // Cancel any pending searches and do immediate search
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      handleSearch(topSuggestion);

      // Trigger autocomplete for the new query to show additional suggestions
      setTimeout(() => {
        fetchAutocomplete(completedQuery);
      }, 100);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    // Cancel any pending debounced searches
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    if (autocompleteTimeout) {
      clearTimeout(autocompleteTimeout);
    }

    setQuery(suggestion);
    setShowAutocomplete(false);
    handleSearch(suggestion);
  };

  const handleSearch = async (query: string) => {
    if (!query) {
      setResults([]);
      setShowResults(false);
      return;
    }

    // Increment sequence to track this request
    const currentSequence = searchSequence + 1;
    setSearchSequence(currentSequence);

    try {
      // Calculate expanded facets for include_facets parameter
      const expandedFacetsForSearch: { [key: string]: string[][] } = {};
      Object.entries(expandedFacets).forEach(([field, pathMap]) => {
        const expandedPaths = Object.keys(pathMap)
          .filter((pathKey) => pathMap[pathKey])
          .map((pathKey) => pathKey.split('/'));
        if (expandedPaths.length > 0) {
          expandedFacetsForSearch[field] = expandedPaths;
        }
      });

      const searchParams = {
        query,
        selectedFacets: mergeBooleanFacets(selectedFacets, booleanFacets),
        selectedPriceRange,
        expandedFacets: expandedFacetsForSearch,
        sort: selectedSort,
        booleanFacets: null,
      };

      const data = await apiClient.search(searchParams);

      // Only update UI if this result is more recent than what we currently have
      if (currentSequence >= searchSequence) {
        setResults(data.hits);
        setShowResults(true);
        setFacets(data.facets || []);
      }
      // If currentSequence < searchSequence, this is a stale response - ignore it

      // Price range is now handled directly by the NumericRangeFacet component
    } catch (error) {
      console.error('Search error:', error);
      // Only update UI if this result is more recent than what we currently have
      if (currentSequence >= searchSequence) {
        setResults([]);
        setShowResults(false);
      }
    }
  };

  const handleFacetClick = (fieldName: string, facetPath: string[]) => {
    // Calculate new facet state (add or remove hierarchical path)
    const prev = selectedFacets[fieldName] || [];
    let newValues;

    // Check if this exact path is already selected
    const isAlreadySelected = prev.some(
      (selectedPath) =>
        selectedPath.length === facetPath.length &&
        selectedPath.every((segment, index) => segment === facetPath[index])
    );

    if (isAlreadySelected) {
      // Remove this path
      newValues = prev.filter(
        (selectedPath) =>
          !(
            selectedPath.length === facetPath.length &&
            selectedPath.every((segment, index) => segment === facetPath[index])
          )
      );
    } else {
      // Add this path and remove any selected children or parents
      // Filter out descendants (children) of the selected path
      const withoutChildren = prev.filter(
        (selectedPath) =>
          !(
            selectedPath.length > facetPath.length &&
            selectedPath
              .slice(0, facetPath.length)
              .every((segment, index) => segment === facetPath[index])
          )
      );
      // Filter out ancestors (parents) of the selected path
      const withoutParents = withoutChildren.filter(
        (selectedPath) =>
          !(
            selectedPath.length < facetPath.length &&
            facetPath
              .slice(0, selectedPath.length)
              .every((segment, index) => segment === selectedPath[index])
          )
      );
      newValues = [...withoutParents, facetPath];
    }

    const newSelectedFacets = {
      ...selectedFacets,
      [fieldName]: newValues,
    };
    setSelectedFacets(newSelectedFacets);

    // Keep hierarchical paths as arrays for proper backend processing
    const hierarchicalFacets: { [key: string]: string[][] } = newSelectedFacets;

    // Calculate expanded facets for include_facets parameter
    const expandedFacetsForSearch: { [key: string]: string[][] } = {};
    Object.entries(expandedFacets).forEach(([field, pathMap]) => {
      const expandedPaths = Object.keys(pathMap)
        .filter((pathKey) => pathMap[pathKey])
        .map((pathKey) => pathKey.split('/'));
      if (expandedPaths.length > 0) {
        expandedFacetsForSearch[field] = expandedPaths;
      }
    });

    // Perform search with new facet state (include sort and boolean filters)
    const searchParams = {
      query,
      selectedFacets: mergeBooleanFacets(hierarchicalFacets, booleanFacets),
      selectedPriceRange,
      expandedFacets: expandedFacetsForSearch,
      sort: selectedSort,
      booleanFacets: null,
    };

    apiClient
      .search(searchParams)
      .then((data) => {
        setResults(data.hits);
        setShowResults(true);
        setFacets(data.facets || []);
      })
      .catch((error) => {
        console.error('Search error:', error);
        setResults([]);
        setShowResults(false);
      });
  };

  const clearAllFilters = () => {
    setSelectedFacets({});
    setSelectedPriceRange(null);
    setUserChangedPrice(false);

    // Reset boolean filters and sort
    setBooleanFacets({});
    setSelectedSort('relevance');

    // Perform search with no filters
    const searchParams = {
      query,
      selectedFacets: {},
      selectedPriceRange: null,
      expandedFacets: {},
      sort: 'relevance',
      booleanFacets: null,
    };

    apiClient
      .search(searchParams)
      .then((data) => {
        setResults(data.hits);
        setShowResults(true);
        setFacets(data.facets || []);
      })
      .catch((error) => {
        console.error('Search error:', error);
      });
  };

  const hasActiveFilters = () => {
    const hasFacetFilters = Object.values(selectedFacets).some(
      (values) => values.length > 0
    );
    const hasBoolean = Object.values(booleanFacets || {}).some(
      (v) => v === true
    );
    return hasFacetFilters || selectedPriceRange !== null || hasBoolean;
  };

  const performSearchWithOverrides = (
    overrides: {
      sort?: string;
      booleanFacets?: { [key: string]: boolean } | null;
      query?: string;
    } = {}
  ) => {
    const q = overrides.query !== undefined ? overrides.query : query;

    // Build expanded facets for search
    const expandedFacetsForSearch: { [key: string]: string[][] } = {};
    Object.entries(expandedFacets).forEach(([field, pathMap]) => {
      const expandedPaths = Object.keys(pathMap)
        .filter((pathKey) => pathMap[pathKey])
        .map((pathKey) => pathKey.split('/'));
      if (expandedPaths.length > 0) {
        expandedFacetsForSearch[field] = expandedPaths;
      }
    });

    const effectiveBooleanFacets =
      overrides.booleanFacets !== undefined
        ? overrides.booleanFacets
        : booleanFacets;
    const searchParams = {
      query: q,
      selectedFacets: mergeBooleanFacets(
        selectedFacets,
        effectiveBooleanFacets
      ),
      selectedPriceRange,
      expandedFacets: expandedFacetsForSearch,
      sort: overrides.sort !== undefined ? overrides.sort : selectedSort,
      booleanFacets: null,
    };

    apiClient
      .search(searchParams)
      .then((data) => {
        setResults(data.hits);
        setShowResults(true);
        setFacets(data.facets || []);
      })
      .catch((error) => {
        console.error('Search error:', error);
        setResults([]);
        setShowResults(false);
      });
  };

  const handleToggleExpansion = (fieldName: string, facetPath: string[]) => {
    const pathKey = facetPath.join('/');
    const currentExpanded = expandedFacets[fieldName]?.[pathKey] || false;

    const newExpandedFacets = {
      ...expandedFacets,
      [fieldName]: {
        ...expandedFacets[fieldName],
        [pathKey]: !currentExpanded,
      },
    };
    setExpandedFacets(newExpandedFacets);

    // Calculate expanded facets for include_facets parameter
    const expandedFacetsForSearch: { [key: string]: string[][] } = {};
    Object.entries(newExpandedFacets).forEach(([field, pathMap]) => {
      const expandedPaths = Object.keys(pathMap)
        .filter((pathKey) => pathMap[pathKey])
        .map((pathKey) => pathKey.split('/'));
      if (expandedPaths.length > 0) {
        expandedFacetsForSearch[field] = expandedPaths;
      }
    });

    // Perform search to get expanded facet data
    const searchParams = {
      query,
      selectedFacets: mergeBooleanFacets(selectedFacets, booleanFacets),
      selectedPriceRange,
      expandedFacets: expandedFacetsForSearch,
      sort: selectedSort,
      booleanFacets: null,
    };

    apiClient
      .search(searchParams)
      .then((data) => {
        setResults(data.hits);
        setShowResults(true);
        setFacets(data.facets || []);
      })
      .catch((error) => {
        console.error('Search error:', error);
      });
  };

  const toggleSpeechRecognition = () => {
    if (!('webkitSpeechRecognition' in window)) {
      console.warn('Speech recognition not supported');
      return;
    }
    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.start();
      setIsListening(true);
      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results[0][0].transcript;
        setQuery(transcript);
        setIsListening(false);
        handleSearch(transcript);
      };
      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      recognition.onend = () => {
        setIsListening(false);
      };
    }
  };

  const handlePriceRangeChange = (newRange: [number, number]) => {
    setSelectedPriceRange(newRange);
    setUserChangedPrice(true);
  };

  const handleAddToCart = async (
    id: string,
    quantity: number = 1
  ): Promise<boolean> => {
    if (!props.addToCartEndpoint) {
      console.warn('addToCartEndpoint non configurato');
      return false;
    }

    if (!id || id.trim() === '') {
      console.error('ID prodotto non valido');
      return false;
    }

    if (quantity < 1) {
      console.error('Quantita deve essere almeno 1');
      return false;
    }

    try {
      const params = new URLSearchParams();
      params.append('data[qty]', quantity.toString());
      params.append('data[id]', id);

      const response = await fetch(props.addToCartEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Errore aggiunta al carrello (${response.status}): ${errorText}`
        );
      }

      console.log(
        `Prodotto ${id} (quantita: ${quantity}) aggiunto al carrello`
      );
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Errore sconosciuto';
      console.error('Errore durante aggiunta al carrello:', errorMessage);
      return false;
    }
  };

  // Trigger search only if user changed the price range
  useEffect(() => {
    if (showResults && userChangedPrice) {
      handleSearch(query);
      setUserChangedPrice(false);
    }
  }, [selectedPriceRange]);

  // Handle escape key for mobile facet modal
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showMobileFacets) {
        setShowMobileFacets(false);
      }
    };

    if (showMobileFacets) {
      document.addEventListener('keydown', handleKeydown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = '';
    };
  }, [showMobileFacets]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(e.target as Node)
      ) {
        sortDropdownRef.current.classList.remove('open');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (autocompleteTimeout) {
        clearTimeout(autocompleteTimeout);
      }
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [autocompleteTimeout, searchTimeout]);

  // Notify state changes (for debug/bookmarking features)
  useEffect(() => {
    if (props.onStateChange) {
      props.onStateChange({
        query,
        selectedFacets,
        selectedPriceRange,
        expandedFacets,
        selectedSort,
        booleanFacets,
      });
    }
  }, [
    query,
    selectedFacets,
    selectedPriceRange,
    expandedFacets,
    selectedSort,
    booleanFacets,
  ]);

  // Trigger initial search if initial state is provided
  useEffect(() => {
    if (
      props.initialState?.query ||
      (props.initialState?.selectedFacets &&
        Object.keys(props.initialState.selectedFacets).length > 0) ||
      (props.initialState?.expandedFacets &&
        Object.keys(props.initialState.expandedFacets).length > 0)
    ) {
      handleSearch(props.initialState.query || '');
    }
  }, []);

  // Intercept add-to-cart button clicks within results
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest('.add-to-cart');
      if (!btn) return;

      const productId = btn.getAttribute('data-product-id');
      if (!productId) return;

      const quantity = parseInt(btn.getAttribute('data-quantity') || '1', 10);

      e.preventDefault();

      handleAddToCart(productId, quantity);
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [results]); // si ricollega quando cambiano i risultati

  return (
    <div
      id="ff-arno-wm-container"
      ref={containerRef}
      className={isFullscreen ? 'fullscreen' : ''}
    >
      <div className="search-bar-container" id="ff-arno-wm" ref={innerRef}>
        <div className="search-bar-header">
          {props.logoUrl && (
            <div className="search-bar-logo">
              <img src={props.logoUrl} alt="Logo" />
            </div>
          )}
          <button
            className={`facet-toggle-btn ${showResults ? '' : 'hidden'}`}
            onClick={() => setShowMobileFacets((prev) => !prev)}
            disabled={!showResults}
          >
            {ICON_TOGGLE_FACETS}
          </button>
          <div className="search-bar-input-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-bar-input"
                placeholder={props.placeholder}
                value={query}
                onInput={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              {topSuggestion &&
                topSuggestion.toLowerCase().startsWith(query.toLowerCase()) &&
                query.length > 0 && (
                  <div className="suggestion-overlay suggestion-overlay-desktop">
                    <span className="typed-text">{query}</span>
                    <span className="suggestion-text">
                      {topSuggestion.slice(query.length)}
                    </span>
                  </div>
                )}
            </div>
            {props.enableSpeech && (
              <button
                className={`search-bar-speech-button ${isListening ? 'listening' : ''}`}
                onClick={toggleSpeechRecognition}
                title={isListening ? 'Stop listening' : 'Start voice search'}
              >
                {ICON_SPEECH_RECOGNITION}
              </button>
            )}
          </div>
          <button
            className={`search-bar-close-button`}
            onClick={closeFullscreen}
          >
            {ICON_CLOSE}
          </button>
        </div>

        {showAutocomplete && autocompleteSuggestions.length > 0 && (
          <div className="autocomplete-suggestions-wrapper">
            <div className="autocomplete-suggestions">
              {ICON_FLASH}
              {autocompleteSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className={`suggestion-chip ${suggestion.is_high_confidence ? 'high-confidence' : ''}`}
                  onClick={() => handleSuggestionClick(suggestion.text)}
                  title={`${suggestion.frequency} occurrences, ${suggestion.confidence.toFixed(1)}% confidence`}
                >
                  <span className="suggestion-text">{suggestion.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {showResults && (
          <div className="search-bar-results">
            <div className="search-bar-facets">
              {hasActiveFilters() && (
                <button
                  className="clear-filters-button"
                  onClick={clearAllFilters}
                >
                  {ICON_CLEAR}                  
                  Cancella filtri
                </button>
              )}
              {(() => {
                const booleanFacetsList = facets.filter(
                  (f) => f.facet_type.type === 'boolean'
                );
                if (booleanFacetsList.length === 0) return null;
                return (
                  <div
                    className="facet-group boolean-facet-list"
                    style={{ marginTop: '12px' }}
                  >
                    {booleanFacetsList.map((facet) => {
                      const count =
                        facet.facet_type.values?.find(
                          (v) => v.path[0] === 'true'
                        )?.count ?? 0;
                      const disabled = count === 0;
                      return (
                        <BooleanFacet
                          key={facet.field_name}
                          label={facet.display_name}
                          checked={!!booleanFacets[facet.field_name]}
                          onToggle={() => {
                            const newFilters = {
                              ...booleanFacets,
                              [facet.field_name]:
                                !booleanFacets[facet.field_name],
                            };
                            setBooleanFacets(newFilters);
                            performSearchWithOverrides({
                              booleanFacets: newFilters,
                            });
                          }}
                          icon={ICON_FACETS[facet.field_name]}
                          count={count}
                          showCount={true}
                          disabled={disabled}
                        />
                      );
                    })}
                  </div>
                );
              })()}
              {facets.map((facetGroup) => {
                if (facetGroup.facet_type.type === 'categorical') {
                  return (
                    <CategoricalFacet
                      key={facetGroup.field_name}
                      facetGroup={facetGroup}
                      selectedFacets={selectedFacets}
                      expandedFacets={expandedFacets}
                      onFacetClick={handleFacetClick}
                      onToggleExpansion={handleToggleExpansion}
                    />
                  );
                } else if (facetGroup.facet_type.type === 'numeric') {
                  return (
                    <NumericRangeFacet
                      key={facetGroup.field_name}
                      facetGroup={facetGroup}
                      selectedPriceRange={selectedPriceRange}
                      onPriceRangeChange={handlePriceRangeChange}
                    />
                  );
                }
                return null;
              })}
            </div>
            <div className="results-controls">
              <div ref={sortDropdownRef} className="sort-dropdown">
                <button
                  className="sort-toggle"
                  onClick={() => {
                    const el = sortDropdownRef.current;
                    if (el) el.classList.toggle('open');
                  }}
                  aria-haspopup="listbox"
                >
                  Ordina:{' '}
                  {selectedSort === 'relevance'
                    ? 'Rilevanza'
                    : selectedSort === 'price_asc'
                      ? 'Prezzo ↑'
                      : selectedSort === 'price_desc'
                        ? 'Prezzo ↓'
                        : selectedSort}
                  <span className="caret">▾</span>
                </button>
                <div className="sort-options" role="listbox">
                  <button
                    onClick={() => {
                      setSelectedSort('relevance');
                      const el = sortDropdownRef.current;
                      if (el) el.classList.remove('open');
                      performSearchWithOverrides({ sort: 'relevance' });
                    }}
                  >
                    Rilevanza
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSort('price_asc');
                      const el = sortDropdownRef.current;
                      if (el) el.classList.remove('open');
                      performSearchWithOverrides({ sort: 'price_asc' });
                    }}
                  >
                    Prezzo: Crescente
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSort('price_desc');
                      const el = sortDropdownRef.current;
                      if (el) el.classList.remove('open');
                      performSearchWithOverrides({ sort: 'price_desc' });
                    }}
                  >
                    Prezzo: Decrescente
                  </button>
                </div>
              </div>
            </div>
            <div className="search-bar-results-grid">
              {results.map((hit) => {
                const resultTemplate =
                  props.resultTemplate ||
                  createResultTemplate(props.addToCartEndpoint);
                return (
                  <div key={hit.id} className="search-bar-result-item">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: resultTemplate(hit),
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Mobile facet modal */}
        {showMobileFacets && (
          <div
            className="facet-modal active"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowMobileFacets(false);
              }
            }}
          >
            <div className="facet-modal-content">
              <div className="facet-modal-header">
                <h3 className="facet-modal-title">Filtri</h3>
                <button
                  className="facet-modal-close"
                  onClick={() => setShowMobileFacets(false)}
                >
                  {ICON_BACK}                  
                </button>
              </div>
              <div className="facet-modal-body">
                {(() => {
                  const booleanFacetsList = facets.filter(
                    (f) => f.facet_type.type === 'boolean'
                  );
                  if (booleanFacetsList.length === 0) return null;
                  return (
                    <div
                      className="facet-group boolean-facet-list"
                      style={{ marginTop: '12px' }}
                    >
                      {booleanFacetsList.map((facet) => {
                        const count =
                          facet.facet_type.values?.find(
                            (v) => v.path[0] === 'true'
                          )?.count ?? 0;
                        const disabled = count === 0;
                        return (
                          <BooleanFacet
                            key={facet.field_name}
                            label={facet.display_name}
                            checked={!!booleanFacets[facet.field_name]}
                            onToggle={() => {
                              const newFilters = {
                                ...booleanFacets,
                                [facet.field_name]:
                                  !booleanFacets[facet.field_name],
                              };
                              setBooleanFacets(newFilters);
                              performSearchWithOverrides({
                                booleanFacets: newFilters,
                              });
                            }}
                            icon={ICON_FACETS[facet.field_name]}
                            count={count}
                            showCount={true}
                            disabled={disabled}
                          />
                        );
                      })}
                    </div>
                  );
                })()}
                {facets.map((facetGroup) => {
                  if (facetGroup.facet_type.type === 'categorical') {
                    return (
                      <CategoricalFacet
                        key={facetGroup.field_name}
                        facetGroup={facetGroup}
                        selectedFacets={selectedFacets}
                        expandedFacets={expandedFacets}
                        onFacetClick={handleFacetClick}
                        onToggleExpansion={handleToggleExpansion}
                      />
                    );
                  } else if (facetGroup.facet_type.type === 'numeric') {
                    return (
                      <NumericRangeFacet
                        key={facetGroup.field_name}
                        facetGroup={facetGroup}
                        selectedPriceRange={selectedPriceRange}
                        onPriceRangeChange={handlePriceRangeChange}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          </div>
        )}
        <div class="powered-by">
          <span>Search powered by </span>          
          <a href="https://www.dino-search.com" target="_blank" title="Dino - Fast search engine solution for e-commerce">
            <img src="https://www.webmotion.it/playground/dino.svg" alt="Dino - Logo" />
          </a>
        </div>
      </div>
    </div>
  );
}

export class SearchBar {
  private options: SearchBarOptions;
  private container: HTMLElement;

  constructor(options: SearchBarOptions) {
    this.options = options;
    //const container = document.querySelector(options.container);
    //if (!container) {
    //throw new Error(`Container element ${options.container} not found`);
    //}

    const searchContainer = document.createElement('div');
    document.body.appendChild(searchContainer);

    this.container = searchContainer as HTMLElement;
    const allOptions = {
      enableSpeech: true,
      ...this.options,
    };
    render(<SearchBarComponent {...allOptions} />, this.container);
  }
}

// TypeScript declarations for the Web Speech API
interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onaudiostart: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  onaudioend: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  onend: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  onerror:
    | ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => unknown)
    | null;
  onnomatch:
    | ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => unknown)
    | null;
  onresult:
    | ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => unknown)
    | null;
  onsoundstart: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  onsoundend: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  serviceURI: string;
  abort(): void;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionStatic {
  new (): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionStatic;
    webkitSpeechRecognition: SpeechRecognitionStatic;
  }
}
